import clinicInfo from '../config/clinicInfo';
import { supabase } from '../config/supabase';
import type { BankingInfo } from '../types/banking';

interface ClientBankingSettingsRow {
  clientId: string;
  bankName: string | null;
  accountType: string | null;
  agency: string | null;
  account: string | null;
  pixKey: string | null;
  pixType: string | null;
  beneficiary: string | null;
}

const STORAGE_KEY_PREFIX = 'client-banking-settings:';

export class ClientBankingSettingsService {
  private static bankingSettingsTableAvailable: boolean | null = null;
  private static missingTableWarningShown = false;

  static getDefaultBankingInfo(): BankingInfo {
    return {
      bankName: clinicInfo.bancarios.banco,
      accountType: 'Conta Corrente',
      agency: clinicInfo.bancarios.agencia,
      account: clinicInfo.bancarios.conta,
      pixKey: clinicInfo.bancarios.pix,
      pixType: clinicInfo.bancarios.pixTipo ?? 'Não Informado',
      beneficiary: clinicInfo.bancarios.beneficiario,
    };
  }

  private static getStorageKey(clientId: string): string {
    return `${STORAGE_KEY_PREFIX}${clientId}`;
  }

  private static normalizeBankingInfo(input?: Partial<BankingInfo> | null): BankingInfo {
    const defaults = this.getDefaultBankingInfo();

    return {
      bankName: input?.bankName?.trim() || defaults.bankName,
      accountType: input?.accountType?.trim() || defaults.accountType,
      agency: input?.agency?.trim() || defaults.agency,
      account: input?.account?.trim() || defaults.account,
      pixKey: input?.pixKey?.trim() || defaults.pixKey,
      pixType: input?.pixType?.trim() || defaults.pixType,
      beneficiary: input?.beneficiary?.trim() || defaults.beneficiary,
    };
  }

  private static mapRowToBankingInfo(row: ClientBankingSettingsRow | null): BankingInfo {
    return this.normalizeBankingInfo(row ?? undefined);
  }

  private static readFromLocalStorage(clientId: string): BankingInfo | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const rawValue = window.localStorage.getItem(this.getStorageKey(clientId));
      if (!rawValue) {
        return null;
      }

      return this.normalizeBankingInfo(JSON.parse(rawValue) as Partial<BankingInfo>);
    } catch (error) {
      console.warn('Não foi possível ler os dados bancários do armazenamento local:', error);
      return null;
    }
  }

  private static writeToLocalStorage(clientId: string, bankingInfo: BankingInfo): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(this.getStorageKey(clientId), JSON.stringify(bankingInfo));
    } catch (error) {
      console.warn('Não foi possível salvar os dados bancários no armazenamento local:', error);
    }
  }

  private static isTableMissingError(error: any): boolean {
    const message = String(error?.message || '').toLowerCase();
    const details = String(error?.details || '').toLowerCase();
    const code = String(error?.code || '').toLowerCase();
    const status = Number(error?.status || 0);

    return (
      status === 404 ||
      code === 'pgrst205' ||
      message.includes('could not find the table') ||
      details.includes('could not find the table')
    );
  }

  private static markTableUnavailable(error: any): void {
    this.bankingSettingsTableAvailable = false;
    if (this.missingTableWarningShown) return;
    this.missingTableWarningShown = true;
    console.warn(
      'Tabela public.client_banking_settings não encontrada no Supabase. Usando fallback local até a migration ser aplicada.',
      error,
    );
  }

  static async getByClientId(clientId: string): Promise<BankingInfo> {
    const localFallback = this.readFromLocalStorage(clientId);
    if (this.bankingSettingsTableAvailable === false) {
      return localFallback ?? this.getDefaultBankingInfo();
    }

    try {
      const { data, error } = await supabase
        .from('client_banking_settings')
        .select('clientId, bankName, accountType, agency, account, pixKey, pixType, beneficiary')
        .eq('clientId', clientId)
        .maybeSingle();

      if (error) {
        if (this.isTableMissingError(error)) {
          this.markTableUnavailable(error);
          return localFallback ?? this.getDefaultBankingInfo();
        }
        throw error;
      }

      this.bankingSettingsTableAvailable = true;

      if (!data) {
        return localFallback ?? this.getDefaultBankingInfo();
      }

      const normalized = this.mapRowToBankingInfo(data as ClientBankingSettingsRow);
      this.writeToLocalStorage(clientId, normalized);
      return normalized;
    } catch (error) {
      console.warn('Não foi possível carregar os dados bancários do Supabase. Usando fallback local.', error);
      return localFallback ?? this.getDefaultBankingInfo();
    }
  }

  static async upsertForClient(clientId: string, bankingInfo: Partial<BankingInfo>): Promise<BankingInfo> {
    const normalized = this.normalizeBankingInfo(bankingInfo);
    this.writeToLocalStorage(clientId, normalized);
    if (this.bankingSettingsTableAvailable === false) {
      return normalized;
    }

    try {
      const { data, error } = await supabase
        .from('client_banking_settings')
        .upsert(
          {
            clientId,
            bankName: normalized.bankName,
            accountType: normalized.accountType,
            agency: normalized.agency,
            account: normalized.account,
            pixKey: normalized.pixKey,
            pixType: normalized.pixType,
            beneficiary: normalized.beneficiary,
          },
          { onConflict: 'clientId' }
        )
        .select('clientId, bankName, accountType, agency, account, pixKey, pixType, beneficiary')
        .single();

      if (error) {
        if (this.isTableMissingError(error)) {
          this.markTableUnavailable(error);
          return normalized;
        }
        throw error;
      }

      this.bankingSettingsTableAvailable = true;

      const persisted = this.mapRowToBankingInfo(data as ClientBankingSettingsRow);
      this.writeToLocalStorage(clientId, persisted);
      return persisted;
    } catch (error) {
      console.warn('Não foi possível persistir os dados bancários no Supabase. Mantendo fallback local.', error);
      return normalized;
    }
  }
}
